import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/atoms/ui/dialog';
import { Button } from '@/components/atoms/ui/button';
import { FiMousePointer } from 'react-icons/fi';

const STORAGE_KEY = 'tuval_right_click_tutorial_dismissed';

interface RightClickTutorialProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RightClickTutorial = ({
  isOpen,
  onClose,
}: RightClickTutorialProps) => {
  const handleClose = () => {
    // Save dismissal state to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.warn('Failed to save tutorial dismissal:', error);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="right-click-tutorial-component max-w-md bg-background/80 backdrop-blur-xl border border-border dark:border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">
            Right-Click to Delete
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-2">
            You can use right-click to delete pending pixels.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-white/10 flex items-center justify-center border border-gray-200 dark:border-white/20">
                <FiMousePointer className="h-6 w-6 text-gray-700 dark:text-white rotate-45" />
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                After placing a pixel, if you haven't applied it yet, you can
                undo the pending change by{' '}
                <span className="font-semibold text-foreground">
                  right-clicking
                </span>
                .
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Left click: Place pixel</li>
                <li>Right click: Delete pending pixel</li>
                <li>Middle click: Pan</li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-3 border border-gray-200 dark:border-white/10">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Tip:</span> If you
              have multiple pending pixels, you can delete them all by
              right-clicking and dragging.
            </p>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleClose}
            className="bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 border border-gray-200 dark:border-white/20 text-gray-900 dark:text-white"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Check if the tutorial has been dismissed
 */
export const isTutorialDismissed = (): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    return dismissed === 'true';
  } catch (error) {
    console.warn('Failed to read tutorial dismissal state:', error);
    return false;
  }
};

